import { Mesh, MeshBasicMaterial, Color } from "three";
import VUtil from "../VUtils";

export class VBase extends Mesh {
  constructor(options, vuilib) {
    options = options || {};
    if (typeof options !== "object") {
      console.warn("VBase constructor expects options as object");
      options = {};
    }

    // 合并属性初始化逻辑
    const initProps = (propGroups) => {
      return propGroups.reduce((acc, [key, defaultValue]) => {
        acc[key] = options[key] !== undefined ? options[key] : defaultValue;
        return acc;
      }, {});
    };

    // 基础属性 所有子类共有的
    const baseProps = initProps([
      ["color", 0xffffff], // 颜色
      ["map", null], // 贴图
      ["opacity", 1], // 透明度
      ["renderOrder", 99999], // 渲染顺序
      ["priority", 1], // 优先级
      ["width", 1], // 宽度
      ["height", 1], // 高度
      ["radius", 0.01], // 圆角半径
      ["enabled", true], // 是否启用
    ]);

    // 事件相关属性，某些UI可能没有事件
    const eventProps = {
      hoverEnterEvent: [], // 光标进入事件，带有一个参数，ui对象本身
      hoverExitEvent: [], // 光标离开事件，带有一个参数，ui对象本身
      clickEvent: [], // 点击事件，带有一个参数，ui对象本身
      enabledEvent: [], // 设置enable的回调函数，参数为ui对象本身和enable状态
      checkSelectableEvent: [], // 检测光标位置是否算当前UI的响应函数
    };

    if (options.textLimit === undefined) options.textLimit = 0;
    options.textLimit = Math.floor(Math.max(0, options.textLimit));
    // 文字相关属性，某些UI可能没有文字
    const textProps = initProps([
      ["text", ""], // 文字内容
      ["textLimit", 0], // 文字限制，0表示不限制
      ["textSize", 0.03], // 文字大小
      ["textColor", 0x000000], // 文字颜色
      ["outlineColor", 0x000000], // 文字描边颜色
      ["outlineWidth", 0.001], // 文字描边宽度
      ["font", "normal"], // 字体 "normal"-正常  "bold"-粗体
      ["align", "center"], // 文字对齐方式 "left","center","right"
    ]);

    // 状态相关属性，某些UI可能没有状态
    const stateProps = initProps([
      ["disabledColor", 0xc8c8c8], // 禁用颜色
      ["stateChangeImage", null], // 状态改变时的贴图对象，可以包含3个状态（"normal","hover","click"）也可以只设置其中某些状态
      ["stateChangeColor", null], // 状态改变时的颜色对象，可以包含3个状态（"normal","hover","click"）也可以只设置其中某些状态
    ]);

    const geo = VUtil.createRectangleGeometry(
      baseProps.width,
      baseProps.height,
      baseProps.radius
    );
    const mat = new MeshBasicMaterial({
      color: baseProps.color,
      map: baseProps.map,
      transparent: true,
      opacity: baseProps.opacity,
      depthWrite: baseProps.opacity <= 0.01 ? false : true,
    });
    VUtil.convertSRGBToLinear(mat);
    super(geo, mat);

    this.renderOrder = baseProps.renderOrder;

    this.vuilib = vuilib;
    this._priority = baseProps.priority;
    this._vType = "VBase";
    this.depthTest = false; // 是否需要深度检测
    mat.depthTest = this.depthTest; // 是否需要深度检测

    this._baseProps = baseProps; // 基础属性 所有子类共有的
    this._eventProps = eventProps; // 事件相关属性，某些UI可能没有事件
    this._textProps = textProps; // 文字相关属性，某些UI可能没有文字
    this._stateProps = stateProps; // 状态相关属性，某些UI可能没有状态
    this._otherProps = {}; // 其他属性，各类UI对象独有的

    if (this.vuilib) {
      this.vuilib.addComponents(this, this._baseProps.priority);
    }

    // 方便外部访问这些属性
    this.width = this._baseProps.width;
    this.height = this._baseProps.height;
  }

  /**
   * 给Escy插件使用的光标进入事件，其他地方不需要主动掉用这个函数
   */
  onEcsyHoverEnter() {
    this.onHoverEnter();
  }

  /**
   * 给Escy插件使用的光标离开事件，其他地方不需要主动掉用这个函数
   */
  onEcsyHoverExit() {
    this.onHoverExit();
  }

  /**
   * 给Escy插件使用的检测当前光标位置是否能响应，其他地方不需要主动掉用这个函数
   * @param {*} worldPos
   * @returns
   */
  onEcsycheckSelectable(worldPos) {
    return this.checkSelectable(worldPos);
  }

  /**
   * 给VUILIB使用的点击事件，其他地方不需要主动掉用这个函数
   */
  onVRClick(obj, intersection) {
    this.onSelectEnd(obj, intersection);
  }

  clear() {
    this._traverseDispose(this);

    // 显式解除引用
    this._baseProps = null;
    this._textProps = null;
    this._stateProps = null;
    this._eventProps = null;
    this._otherProps = null;

    // 清理Three.js对象
    if (this.geometry) {
      this.geometry.dispose();
      this.geometry = null;
    }
    if (this.material) {
      this.material.dispose();
      this.material = null;
    }
  }

  /**
   * 添加光标进入事件，参数为UI对象
   * @param {*} fun
   */
  addHoverEnterEvent(fun) {
    if (!fun) return;
    this._eventProps.hoverEnterEvent.push(fun);
  }

  /**
   * 移除一个指定的光标进入事件
   * @param {*} fun
   */
  removeHoverEnterEvent(fun) {
    if (!fun) return;
    let index = this._eventProps.hoverEnterEvent.indexOf(fun);
    if (index >= 0) {
      this._eventProps.hoverEnterEvent.splice(index, 1);
    }
  }

  /**
   * 移除所有光标进入事件
   */
  removeAllHoverEnterEvent() {
    this._eventProps.hoverEnterEvent = [];
  }

  /**
   * 添加光标离开事件，参数为UI对象
   * @param {*} fun
   */
  addHoverExitEvent(fun) {
    if (!fun) return;
    this._eventProps.hoverExitEvent.push(fun);
  }

  /**
   * 移除一个指定的光标离开事件
   * @param {*} fun
   */
  removeHoverExitEvent(fun) {
    if (!fun) return;
    let index = this._eventProps.hoverExitEvent.indexOf(fun);
    if (index >= 0) {
      this._eventProps.hoverExitEvent.splice(index, 1);
    }
  }

  /**
   * 移除所有光标离开事件
   */
  removeAllHoverExitEvent() {
    this._eventProps.hoverExitEvent = [];
  }

  /**
   * 添加点击事件，参数为UI对象
   * @param {*} fun
   */
  addClickEvent(fun) {
    if (!fun) return;
    this._eventProps.clickEvent.push(fun);
  }

  /**
   * 移除一个指定的点击事件
   * @param {*} fun
   */
  removeClickEvent(fun) {
    if (!fun) return;
    let index = this._eventProps.clickEvent.indexOf(fun);
    if (index >= 0) {
      this._eventProps.clickEvent.splice(index, 1);
    }
  }

  /**
   * 移除所有点击事件
   */
  removeAllClickEvent() {
    this._eventProps.clickEvent = [];
  }

  /**
   * 添加 设置enable之后的回调函数，参数为UI对象本身和enable状态
   * @param {*} fun
   */
  addEnabledEvent(fun) {
    if (!fun) return;
    this._eventProps.enabledEvent.push(fun);
  }

  /**
   * 移除一个指定的设置enable之后的回调函数
   * @param {*} fun
   */
  removeEnabledEvent(fun) {
    if (!fun) return;
    let index = this._eventProps.enabledEvent.indexOf(fun);
    if (index >= 0) {
      this._eventProps.enabledEvent.splice(index, 1);
    }
  }

  /**
   * 移除所有设置enable之后的回调函数
   */
  removeAllEnabledEvent() {
    this._eventProps.enabledEvent = [];
  }

  /**
   * 添加 检测世界坐标是否属于当前UI的回调，回调函数的参数 Vector3世界坐标
   * @param {*} fun
   */
  addCheckSelectableEvent(fun) {
    if (!fun) return;
    this._eventProps.checkSelectableEvent.push(fun);
  }

  /**
   * 移除一个检测世界坐标是否属于当前UI的回调
   * @param {*} fun
   */
  removeCheckSelectableEvent(fun) {
    if (!fun) return;
    let index = this._eventProps.checkSelectableEvent.indexOf(fun);
    if (index >= 0) {
      this._eventProps.checkSelectableEvent.splice(index, 1);
    }
  }

  /**
   *  移除所有检测世界坐标是否属于当前UI的回调
   */
  removeAllCheckSelectableEvent() {
    this._eventProps.checkSelectableEvent = [];
  }

  /**
   * 内部使用
   * @param {*} obj
   */
  _traverseDispose(obj) {
    if (obj) {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => {
            if (m.dispose) m.dispose();
          });
        } else obj.material.dispose();
      }

      if (obj.removeFromParent) {
        obj.removeFromParent();
      }
      if (obj._entity) {
        try {
          obj._entity.removeAllComponents(); // Remove all components on this entity.
          obj._entity.remove(); // Remove this entity from the world.
        } catch (e) {
          //console.log(e);
        }
      }

      if (obj.dispose) obj.dispose();

      if (obj.children && obj.children.length > 0) {
        obj.children.forEach((c) => {
          if (c.clear) c.clear();
          this._traverseDispose(c);
        });
      }
    }
  }

  // 使用现代循环方法优化事件处理
  _callEventHandlers = (handlers, ...args) => {
    if (!handlers) return;
    handlers.forEach((handler) => {
      try {
        handler(...args);
      } catch (e) {
        console.error("Event handler error:", e);
      }
      if (!this._eventProps) return;
    });
  };

  /**
   * 设置此UI对象是否启用
   * @param {*} enabled
   */
  setEnabled(enabled) {
    this._baseProps.enabled = enabled;

    let uiObjs = [];
    this.traverse((node) => {
      if (node._vType && node !== this) {
        uiObjs.push(node);
      }
      if (!enabled) {
        if (node._entity) {
          node._dontCheckEcsy = true; // 不进行光标悬停检测
        }
      } else {
        if (node._entity && node._dontCheckEcsy) delete node._dontCheckEcsy;
      }
    });
    for (var i = 0; i < uiObjs.length; i++) {
      if (uiObjs[i].setEnabled) uiObjs[i].setEnabled(enabled);
    }

    if (enabled) {
      this.material.color = new Color(this._baseProps.color);
    } else {
      this.material.color = new Color(this._stateProps.disabledColor);
    }
    VUtil.convertSRGBToLinear(this.material);

    for (var i = 0; i < this._eventProps.enabledEvent.length; i++) {
      this._eventProps.enabledEvent[i](this, enabled);
    }
  }

  /**
   * 光标进入事件
   */
  onHoverEnter() {
    if (!this.visible || !this._baseProps.enabled) return;

    const { stateChangeImage, stateChangeColor } = this._stateProps;
    if (stateChangeImage?.hover) {
      this.material.map = stateChangeImage.hover;
      this.material.needsUpdate = true;
    }
    if (stateChangeColor?.hover !== undefined) {
      this.material.color.set(stateChangeColor.hover); // 重用Color对象
      VUtil.convertSRGBToLinear(this.material);
    }

    this._callEventHandlers(this._eventProps.hoverEnterEvent, this);
  }

  /**
   * 光标离开事件
   */
  onHoverExit() {
    if (!this.visible || !this._baseProps || !this._baseProps.enabled) return;

    const { stateChangeImage, stateChangeColor } = this._stateProps;
    if (stateChangeImage?.normal) {
      this.material.map = stateChangeImage.normal;
      this.material.needsUpdate = true;
    }
    if (stateChangeColor?.normal !== undefined) {
      this.material.color.set(stateChangeColor.normal); // 重用Color对象
      VUtil.convertSRGBToLinear(this.material);
    }

    this._callEventHandlers(this._eventProps.hoverExitEvent, this);
  }

  /**
   * 鼠标或手柄扳机键按下事件
   */
  onSelectStart() {
    if (!this.visible || !this._baseProps.enabled) return;
  }

  // 在类中添加新方法
  _scheduleRestore(callback) {
    setTimeout(() => {
      if (this._baseProps?.enabled && this._stateProps) {
        callback();
      }
    }, 100);
  }

  /**
   * 鼠标或手柄扳机键松开事件
   */
  onSelectEnd() {
    if (!this._baseProps || !this.visible || !this._baseProps.enabled) return;

    // 使用事件处理器统一方法
    this._callEventHandlers(this._eventProps?.clickEvent, this);

    if (!this._stateProps) return;

    const { stateChangeImage, stateChangeColor } = this._stateProps || {};
    const restoreState = () => {
      if (!this._stateProps || !this._baseProps) return;

      // 合并状态恢复逻辑
      if (stateChangeImage) {
        this.material.map = stateChangeImage.hover || stateChangeImage.normal;
      }
      if (stateChangeColor) {
        const color = stateChangeColor.hover ?? stateChangeColor.normal;
        if (color !== undefined) {
          this.material.color.set(color).convertSRGBToLinear();
        }
      }
      this.material.needsUpdate = true;
    };

    // 处理点击状态变化
    if (stateChangeImage?.click) {
      this.material.map = stateChangeImage.click;
      this._scheduleRestore(restoreState);
    }

    if (stateChangeColor?.click !== undefined) {
      this.material.color.set(stateChangeColor.click).convertSRGBToLinear();
      this._scheduleRestore(restoreState);
    }
  }

  /**
   * 隐藏UI
   */
  hide() {
    this._vUISrcScale = this.scale.clone();
    this.scale.set(0, 0, 0);
    this.visible = false;

    this.traverse((node) => {
      if (node._entity) {
        node._dontCheckEcsy = true; // 不进行光标悬停检测
      }
    });
  }

  /**
   * 显示UI
   */
  show() {
    this.visible = true;
    if (this._vUISrcScale) {
      this.scale.copy(this._vUISrcScale);
      delete this._vUISrcScale;
    }

    this.traverse((node) => {
      if (node._dontCheckEcsy) {
        delete node._dontCheckEcsy;
      }
    });
  }

  /**
   * 检测是否可选中，在滚动视图中，对于区域外的ui（被模板测试剔除了，无法看见）不能被选中
   * @param {Vector3} worldPos 当前光标的世界坐标
   * @returns
   */
  checkSelectable(worldPos) {
    if (!this.visible || !this._baseProps.enabled) return false;

    let res = true;

    if (this._eventProps.checkSelectableEvent.length > 0) {
      for (var i = 0; i < this._eventProps.checkSelectableEvent.length; i++) {
        const tmp = this._eventProps.checkSelectableEvent[i](worldPos.clone());
        res = res && tmp;
      }
    }

    return res;
  }

  /**
   * 获取可点击的对象
   * @returns
   */
  getClickable() {
    if (this.visible && this._baseProps.enabled) return [this];
    return [];
  }
}
